import React, { useState } from "react"
import Lightbox from "yet-another-react-lightbox"
import i18n from "@/i18n"
import ImagePreview from "./image_preview"
import ImageWithOverlay from "./image_with_overlay"
import type { GalleryImage, VenueImageGalleryProps } from "./types"
import { isVenuePhotoWithUrl } from "./types"

const STYLES = {
  emptyContainer:
    "flex h-60 w-full items-center justify-center bg-gray-200 md:h-96 lg:mx-auto lg:h-[360px] lg:w-[960px]",
  emptyText: "text-gray-400 text-lg",
  singleImage: "h-60 w-full md:h-96 lg:mx-auto lg:h-[360px] lg:w-[960px]",
  multiImageRow: "flex h-60 gap-[10px] md:h-96 lg:mx-auto lg:h-[360px] lg:w-fit",
  primaryImage: "h-full flex-[650] lg:w-[650px] lg:shrink-0",
  secondaryImage: "h-full flex-[300] lg:w-[300px] lg:shrink-0",
  secondaryColumn: "flex flex-[300] flex-col gap-[10px] lg:w-[300px] lg:shrink-0",
  smallImage: "flex-1 lg:h-[175px] lg:w-[300px] lg:shrink-0",
} as const

const EmptyState: React.FC = () => (
  <div className={STYLES.emptyContainer} data-testid="public-venue-gallery-empty">
    <span className={STYLES.emptyText}>{i18n.t("app.public.venue.gallery.empty_state")}</span>
  </div>
)

type GalleryPreviewProps = {
  venuePhotos: GalleryImage[]
  onImageClick: (index: number) => void
}

const SingleImagePreview: React.FC<GalleryPreviewProps> = ({ venuePhotos, onImageClick }) => (
  <ImagePreview photo={venuePhotos[0]} index={0} onImageClick={onImageClick} className={STYLES.singleImage} />
)

const TwoImagesPreview: React.FC<GalleryPreviewProps> = ({ venuePhotos, onImageClick }) => (
  <div className={STYLES.multiImageRow}>
    <ImagePreview photo={venuePhotos[0]} index={0} onImageClick={onImageClick} className={STYLES.primaryImage} />
    <ImagePreview photo={venuePhotos[1]} index={1} onImageClick={onImageClick} className={STYLES.secondaryImage} />
  </div>
)

const ThreeOrMoreImagesPreview: React.FC<GalleryPreviewProps> = ({ venuePhotos, onImageClick }) => (
  <div className={STYLES.multiImageRow}>
    <ImagePreview photo={venuePhotos[0]} index={0} onImageClick={onImageClick} className={STYLES.primaryImage} />
    <div className={STYLES.secondaryColumn}>
      <ImagePreview photo={venuePhotos[1]} index={1} onImageClick={onImageClick} className={STYLES.smallImage} />
      <ImageWithOverlay
        photo={venuePhotos[2]}
        totalCount={venuePhotos.length}
        onImageClick={onImageClick}
        className={STYLES.smallImage}
      />
    </div>
  </div>
)

const VenueImageGalleryDesktop: React.FC<VenueImageGalleryProps> = ({ images }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const venuePhotos = images.filter(isVenuePhotoWithUrl)

  const handleImageClick = (index: number) => {
    setLightboxIndex(index)
    setLightboxOpen(true)
  }

  if (venuePhotos.length === 0) {
    return <EmptyState />
  }

  const renderPreview = () => {
    if (venuePhotos.length === 1) {
      return <SingleImagePreview venuePhotos={venuePhotos} onImageClick={handleImageClick} />
    }
    if (venuePhotos.length === 2) {
      return <TwoImagesPreview venuePhotos={venuePhotos} onImageClick={handleImageClick} />
    }
    return <ThreeOrMoreImagesPreview venuePhotos={venuePhotos} onImageClick={handleImageClick} />
  }

  return (
    <div data-testid="public-venue-gallery-container">
      {renderPreview()}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={venuePhotos.map((photo) => ({ src: photo.url }))}
        carousel={{ padding: "16px" }}
      />
    </div>
  )
}

export default VenueImageGalleryDesktop
